import { type FC, useMemo } from "react";
import { Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";
import { formatNumberByThousands } from "widgets/utils";

import { type AaFeatureStats, type GenomeStatistics, type Organism } from "@/graphql.ts";
import { SelMessage } from "@/messages/SelMessage.tsx";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { useFetchGenomeProteinStatistics } from "@/ui-modules/entities/genomes/graphql/useFetchGenomeStatistics.ts";

export interface IGenomicProteinContentCardProps extends GridCard {
  genomeId: Organism["id"];
}

const GenomicProteinContentCard: FC<IGenomicProteinContentCardProps> = (props) => {
  const { data, loading, error } = useFetchGenomeProteinStatistics({ genomeId: props.genomeId });
  const genomeStatistics: GenomeStatistics = data as GenomeStatistics;

  const aaFeaturesCount = useMemo<AaFeatureStats[]>(
    () => (genomeStatistics?.aaFeaturesCount ?? []) as unknown as AaFeatureStats[],
    [genomeStatistics?.aaFeaturesCount],
  );
  const proteinsCount = useMemo<number>(() => genomeStatistics?.proteinsCount ?? 0, [genomeStatistics?.proteinsCount]);

  return (
    <Card key="genome-info-card" title={"Protein Features"}>
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        // Fallback. Usually graphql errors are handled globally
        <ContentPlaceholder>{error?.message}</ContentPlaceholder>
      ) : aaFeaturesCount.length === 0 && proteinsCount === 0 ? (
        <SelMessage id={"noContent.proteinContent"} />
      ) : (
        <>
          <CardRowSimple
            label=""
            value={["Motifs Assigned", "Percentage", "With Motif", "Motif Coverage"]}
            key={"card-row-motif-labels"}
          />
          {aaFeaturesCount.map((aaFeatureCount) => (
            <CardRowSimple
              label={aaFeatureCount.featureTypeName}
              value={[
                <span key={"count"}>{formatNumberByThousands(aaFeatureCount.count)}</span>,
                <span key={"percentage"}>
                  {proteinsCount !== 0 ? ((aaFeatureCount.count / proteinsCount) * 100).toFixed(2) + "%" : "-"}
                </span>,
                <span key={"with-motif"}>{formatNumberByThousands(aaFeatureCount.associatedProteins)}</span>,
                <span key={"motif-coverage"}>
                  {proteinsCount !== 0
                    ? ((aaFeatureCount.associatedProteins / proteinsCount) * 100).toFixed(2) + "%"
                    : "-"}
                </span>,
              ]}
              key={"card-row-motif-" + aaFeatureCount.featureTypeName}
            />
          ))}
        </>
      )}
    </Card>
  );
};

export { GenomicProteinContentCard };
